<template>
  <div class="page-container animate-fade-in-up">
    <PageHeader icon="el-icon-user" title="请选择评估对象" subtitle="选择一位员工进行评估，系统将基于其行为特征计算离职风险分数" />

    <div class="toolbar">
      <div class="search-bar">
        <i class="el-icon-search search-icon"></i>
        <input
          v-model="searchKey"
          class="search-input"
          placeholder="输入姓名搜索..."
          @input="handleSearch"
        />
        <i v-if="searchKey" class="el-icon-circle-close search-clear" @click="searchKey = ''"></i>
      </div>
      <div class="stat-summary">
        <span class="stat-item">共 {{ employees.length }} 人</span>
        <span class="stat-item stat-high">{{ highRiskCount }} 高风险</span>
        <span class="stat-item stat-medium">{{ mediumRiskCount }} 中风险</span>
        <span class="stat-item stat-low">{{ lowRiskCount }} 低风险</span>
        <span class="stat-item stat-unrated">{{ unratedCount }} 未评估</span>
      </div>
    </div>

    <div class="table-wrapper">
      <el-table :data="filteredEmployees" stripe class="data-table" v-loading="false">
        <el-table-column label="姓名" min-width="140">
          <template slot-scope="scope">
            <div class="name-cell">
              <div class="employee-name">{{ scope.row.real_name }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="dept_name" label="部门" min-width="120">
          <template slot-scope="scope">{{ scope.row.dept_name || '-' }}</template>
        </el-table-column>
        <el-table-column prop="job_title" label="职位" min-width="120">
          <template slot-scope="scope">{{ scope.row.job_title || '-' }}</template>
        </el-table-column>
        <el-table-column label="风险等级" width="110" align="center">
          <template slot-scope="scope">
            <el-tag
              v-if="isEvaluated(scope.row)"
              :type="getRiskTagType(scope.row.latest_risk_level)"
              size="small"
              effect="dark"
            >
              {{ getRiskLabel(scope.row.latest_risk_level) }}
            </el-tag>
            <el-tag v-else type="info" size="small">未评估</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="最近分数" width="90" align="center">
          <template slot-scope="scope">
            <span v-if="isEvaluated(scope.row)" class="score-text">
              {{ scope.row.latest_score }} 分
            </span>
            <span v-else class="score-text muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="210" align="center">
          <template slot-scope="scope">
            <div class="action-group">
              <el-button
                size="mini"
                :type="isEvaluated(scope.row) ? 'warning' : 'primary'"
                round
                class="action-btn"
                :disabled="$store.getters.isCurrentTaskExpired"
                @click="goEvaluate(scope.row.employee_id)"
              >
                {{ isEvaluated(scope.row) ? '重新评估' : '评估' }}
              </el-button>
              <el-button
                size="mini"
                round
                class="action-btn"
                @click="showHistory(scope.row)"
              >
                历史
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <EmptyState v-if="filteredEmployees.length === 0" icon="el-icon-user" title="暂无下属员工" hint="请联系管理员配置您的团队成员" />

    <!-- 评估历史弹窗 -->
    <el-dialog
      :title="historyEmployee ? historyEmployee.real_name + ' 的评估历史' : '评估历史'"
      :visible.sync="historyVisible"
      width="640px"
      :append-to-body="true"
    >
      <div v-if="historyList.length" class="history-list-dialog">
        <div
          v-for="item in historyList"
          :key="item.evaluation_id"
          class="history-dialog-item"
        >
          <div class="history-dialog-left">
            <div class="history-dialog-date">{{ item.created_at }}</div>
            <div class="history-dialog-score">
              <RiskScoreTag :score="item.total_score" :thresholds="thresholds" />
            </div>
          </div>
          <div class="history-dialog-actions">
            <el-button type="text" size="mini" @click="viewReport(item)">查看报告</el-button>
            <el-button type="text" size="mini" @click="goEvaluateFromHistory(item)">以此为基础评估</el-button>
          </div>
        </div>
      </div>
      <div v-else class="empty-state-dialog">
        <i class="el-icon-document" style="font-size: 32px; color: var(--color-border);"></i>
        <p>暂无评估记录</p>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '../components/PageHeader.vue'
import EmptyState from '../components/EmptyState.vue'
import RiskScoreTag from '../components/RiskScoreTag.vue'
import { filterByPinyin } from '../utils/pinyinFilter'

export default {
  name: 'EmployeeSelect',
  components: { PageHeader, EmptyState, RiskScoreTag },
  data() {
    return {
      searchKey: '',
      historyVisible: false,
      historyEmployee: null
    }
  },
  computed: {
    thresholds() {
      return this.$store.state.settings.settings.thresholds || { lowMax: 4, mediumMax: 10 }
    },
    employees() {
      const user = this.$store.state.user
      const email = user && user.email ? user.email : ''
      let list = []
      if (email) {
        list = this.$store.getters.subordinatesByEmail(email) || []
      } else {
        list = this.$store.state.employees.employees || []
      }
      // 只显示在职员工（employment_status === 1）
      return list.filter(e => e.employment_status === 1)
    },
    evaluations() {
      return this.$store.state.evaluations || []
    },
    filteredEmployees() {
      if (!this.searchKey) return this.employees
      const key = this.searchKey.toLowerCase()
      const byPinyin = filterByPinyin(this.employees, this.searchKey)
      if (byPinyin.length > 0 && this.searchKey.length >= 2) return byPinyin
      return this.employees.filter(e =>
        e.real_name.includes(key)
      )
    },
    highRiskCount() {
      return this.employees.filter(e => e.latest_risk_level === 3).length
    },
    mediumRiskCount() {
      return this.employees.filter(e => e.latest_risk_level === 2).length
    },
    lowRiskCount() {
      return this.employees.filter(e => e.latest_risk_level === 1).length
    },
    unratedCount() {
      return this.employees.filter(e => !e.latest_risk_level).length
    },
    historyList() {
      if (!this.historyEmployee) return []
      const taskId = this.$store.state.tasks && this.$store.state.tasks.currentTask && this.$store.state.tasks.currentTask.task_id
      let list = this.evaluations.filter(e => e.employee_id === this.historyEmployee.employee_id)
      if (taskId) {
        list = list.filter(e => e.task_id === taskId || !e.task_id)
      }
      return list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    }
  },
  methods: {
    handleSearch() {
      // debounce can be added here if needed
    },
    isEvaluated(employee) {
      // 统一判断员工是否已评估：后端预计算字段同时有效
      return employee && employee.latest_risk_level && employee.latest_score > 0
    },
    getEvalStatus(employeeId) {
      const taskId = this.$store.state.tasks && this.$store.state.tasks.currentTask && this.$store.state.tasks.currentTask.task_id
      let list = this.evaluations.filter(e => e.employee_id === employeeId)
      if (taskId) {
        list = list.filter(e => e.task_id === taskId || !e.task_id)
      }
      if (list.length === 0) {
        return { hasEval: false, latestScore: 0, riskLevel: 0 }
      }
      const latest = list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
      const score = latest.total_score || 0
      const riskLevel = score > this.thresholds.mediumMax ? 3 : (score > this.thresholds.lowMax ? 2 : 1)
      return { hasEval: true, latestScore: score, riskLevel }
    },
    getRiskTagType(level) {
      const map = { 1: 'success', 2: 'warning', 3: 'danger' }
      return map[level] || 'info'
    },
    getRiskLabel(level) {
      const map = { 1: '低风险', 2: '中风险', 3: '高风险' }
      return map[level] || '-'
    },
    goEvaluate(employeeId) {
      this.$router.push('/evaluation/' + employeeId)
    },
    showHistory(employee) {
      this.historyEmployee = employee
      this.historyVisible = true
    },
    viewReport(item) {
      this.$router.push('/report/' + item.employee_id + '?evaluation_id=' + item.evaluation_id)
    },
    goEvaluateFromHistory(item) {
      this.historyVisible = false
      this.$router.push('/evaluation/' + item.employee_id + '?base_on=' + item.evaluation_id)
    }
  }
}
</script>

<style scoped>
.page-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* 工具栏 */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-lg);
  flex-wrap: wrap;
  gap: var(--space-md);
}

/* 搜索栏 */
.search-bar {
  position: relative;
  width: 320px;
}

.search-icon {
  position: absolute;
  left: var(--space-lg);
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
  font-size: 16px;
}

.search-input {
  width: 100%;
  padding: 10px 36px 10px 40px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  font-size: 14px;
  background: var(--color-surface);
  color: var(--color-text);
  transition: all var(--duration-fast) var(--ease-out);
  outline: none;
}

.search-input:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(242, 201, 76, 0.15);
}

.search-input::placeholder {
  color: var(--color-text-muted);
}

.search-clear {
  position: absolute;
  right: var(--space-md);
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
  cursor: pointer;
  font-size: 16px;
  transition: color var(--duration-fast) var(--ease-out);
}

.search-clear:hover {
  color: var(--color-primary);
}

/* 统计摘要 */
.stat-summary {
  display: flex;
  gap: var(--space-md);
  font-size: 13px;
}

.stat-item {
  padding: 4px 10px;
  border-radius: var(--radius-full);
  background: var(--color-bg);
  color: var(--color-text-secondary);
}

.stat-high {
  background: var(--color-high-bg);
  color: var(--color-high-text);
}

.stat-medium {
  background: var(--color-medium-bg);
  color: var(--color-medium-text);
}

.stat-low {
  background: var(--color-low-bg);
  color: var(--color-low-text);
}

.stat-unrated {
  background: #F4F4F5;
  color: #909399;
}

/* 操作列按钮统一布局 */
.action-group {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.action-btn {
  min-width: 72px;
  white-space: nowrap;
}

/* 表格 */
.table-wrapper {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.data-table >>> th {
  background: var(--color-bg) !important;
  color: var(--color-text) !important;
  font-weight: 600 !important;
}

.name-cell {
  display: flex;
  align-items: center;
}

.employee-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
}

.employee-no {
  font-size: 12px;
  color: var(--color-text-muted);
  margin-top: 2px;
}

.score-text {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
}

.score-text.muted {
  color: var(--color-text-muted);
}

/* 历史弹窗 */
.history-list-dialog {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.history-dialog-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-md) var(--space-lg);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}

.history-dialog-left {
  display: flex;
  align-items: center;
  gap: var(--space-md);
}

.history-dialog-date {
  font-size: 13px;
  color: var(--color-text-secondary);
  min-width: 140px;
}

.history-dialog-score {
  font-size: 14px;
  font-weight: 600;
}

.history-dialog-actions {
  display: flex;
  gap: var(--space-sm);
}

.empty-state-dialog {
  text-align: center;
  padding: var(--space-3xl);
  color: var(--color-text-muted);
}

.empty-state-dialog p {
  margin-top: var(--space-md);
  font-size: 14px;
}

/* 响应式 */
@media (max-width: 768px) {
  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-bar {
    width: 100%;
  }

  .stat-summary {
    flex-wrap: wrap;
  }
}
</style>
